<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			hide-search-query-filter
			hide-brand-filter
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					v-model="domainFilter"
					:items="cookieDomainItems"
					label="Domain"
					custom-sort
				/>
				<Dropdown
					v-model="cookieCategoryFilter"
					:items="cookieCategoryDropdownItems"
					label="Cookie Category"
					custom-sort
					@change="updateOverview"
				/>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Cookies
			</template>
			<template #subtitle>
				All cookies are grouped by domain, click to expand
			</template>
			<template #body>
				<v-expansion-panels
					ref="expansionPanels"
				>
					<v-expansion-panel
						v-for="(cookieDomain, index) in filteredCookieDomains"
						:key="index"
						@click="openCookieDetails($event, cookieDomain.domain)"
					>
						<v-expansion-panel-header>
							<v-row>
								<v-col cols="10">
									<span class="font-weight-bold mr-2">
										{{ cookieDomain.domain }} :
									</span>
									{{ cookieDomain.count }}
								</v-col>
							</v-row>
						</v-expansion-panel-header>
						<v-expansion-panel-content v-if="cookiesByDomain[cookieDomain.domain]">
							<DataTable
								:headers="tableHeaders"
								:items="dataTableItems(cookieDomain)"
								:options.sync="cookiesByDomain[cookieDomain.domain].options"
								:server-items-length="cookiesByDomain[cookieDomain.domain].totalRecords"
								@update:options="updateOptions($event, cookieDomain.domain)"
								@click:row="openCookieDetailsModal"
							>
								<template #item.action="{ item }">
									<v-menu offset-y>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												v-bind="attrs"
												v-on="on"
												@click.stop
											>
												<v-icon>
													mdi-dots-horizontal
												</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item
												v-if="userCanUseSiteScanner"
												@click="openCookieDetailsModal(item)"
											>
												<v-list-item-content>
													<v-list-item-title>
														Edit Cookie Details
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-menu>
								</template>
							</DataTable>
						</v-expansion-panel-content>
					</v-expansion-panel>
				</v-expansion-panels>
			</template>
		</SectionCard>
		<ManageCookieModal
			v-if="showCookieDetailsModal"
			:selected-cookie="selectedCookie"
			:user-can-use-site-scanner="userCanUseSiteScanner"
			:scan-guid="scanGuid"
			:cookie-categories="modalCookieCategories"
			:total-urls="totalUrls"
			@close="closeCookieDetailsModal"
			@update-cookie-details="updateCookieDetails"
		/>
	</div>
</template>

<script>
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import ManageCookieModal from './manage-cookie-modal.vue'
import { getCookieOverview, getCookieLookup } from '../../../../../../../shared/utils/api/site-scanner.js'
export default {
	components: {
		FilterBar,
		Dropdown,
		SectionCard,
		DataTable,
		ManageCookieModal
	},
	props: {
		scanGuid: String,
		userCanUseSiteScanner: Boolean
	},
	data () {
		return {
			cookieDomains: [],
			cookiesByDomain: {},
			showCookieDetailsModal: false,
			openDomain: null,
			cookieCategories: [],
			// Categories actually supplied to the modal (may include ad-hoc/custom ones discovered on a cookie)
			modalCookieCategories: [],
			totalUrls: 0,
			domainFilter: null,
			cookieCategoryFilter: 0
		}
	},
	computed: {
		tableHeaders () {
			return [
				{ text: 'Cookie Name', value: 'cookieName', width: '25%' },
				{ text: 'Category', value: 'category', width: '15%' },
				{ text: 'Description', value: 'description', width: '35%' },
				{ text: 'Classification', value: 'classification', width: '15%' },
				{ text: 'Expiry (In Days)', value: 'expiryInDays', width: '10%' },
				{ text: 'Action', value: 'action', width: '80px', align: 'center', sortable: false }
			]
		},
		cookieDomainItems () {
			const domainItems = [
				{ value: null, text: 'View All' },
				...this.cookieDomains.map(({ domain }) => ({
					value: domain,
					text: domain
				})).sort((a, b) => a.text.localeCompare(b.text))
			]
			return domainItems
		},
		filteredCookieDomains () {
			return this.cookieDomains.filter(({ domain }) => {
				let check = true
				if (this.domainFilter) check = domain.toLowerCase() === this.domainFilter.toLowerCase()
				return check
			})
		},
		cookieCategoryDropdownItems () {
			return this.cookieCategories.map(({ categoryId, categoryName }) => ({
				value: categoryId,
				text: categoryName
			}))
		}
	},
	async created () {
		await this.getCookieOverview()
	},
	methods: {
		async getCookieOverview () {
			const { data } = await getCookieOverview(this.scanGuid, { categoryId: this.cookieCategoryFilter })
			this.cookieDomains = data.results
			this.cookieCategories = data.cookieCategories
			// If a modal is currently open, re-evaluate merged categories to keep in sync
			if (this.showCookieDetailsModal && this.selectedCookie) {
				this.modalCookieCategories = [...this.cookieCategories]
				if (this.selectedCookie.categoryId && !this.modalCookieCategories.some(c => c.categoryId === this.selectedCookie.categoryId)) {
					this.modalCookieCategories.push({
						categoryId: this.selectedCookie.categoryId,
						categoryName: `${this.selectedCookie.category || 'Custom Category'} (Custom)`
					})
				}
				this.modalCookieCategories.sort((a, b) => a.categoryName.localeCompare(b.categoryName))
			}
		},
		openCookieDetails (event, domain) {
			const expansionOpen = event.currentTarget.classList.contains('v-expansion-panel-header--active')
			this.openDomain = domain
			if (!expansionOpen) {
				this.loadCookiesByDomain(domain)
			}
		},
		async loadCookiesByDomain (domain) {
			if (this.cookiesByDomain[domain]) return
			const { data } = await getCookieLookup(this.scanGuid, domain, this.cookieCategoryFilter)
			this.$set(this.cookiesByDomain, domain, data)
		},
		async updateOptions (options, domain) {
			const pageNumber = options.page
			const itemsPerPage = options.itemsPerPage === -1 ? this.cookiesByDomain[domain].totalRecords : options.itemsPerPage
			const orderDirection = options.sortDesc[0] ? 'desc' : 'asc'
			const sortByIndex = this.tableHeaders.findIndex(header => header.value === options.sortBy[0])
			const sortBy = sortByIndex === -1 ? 0 : sortByIndex
			const { data } = await getCookieLookup(this.scanGuid, domain, this.cookieCategoryFilter, itemsPerPage, pageNumber, sortBy, orderDirection)
			const cookieData = {
				...data,
				options
			}
			this.$set(this.cookiesByDomain, domain, cookieData)
		},
		openCookieDetailsModal (cookie) {
			this.totalUrls = this.cookiesByDomain[this.openDomain].totalUrls
			this.selectedCookie = JSON.parse(JSON.stringify(cookie))
			// Prepare category list for modal. Start with authoritative categories returned by overview.
			this.modalCookieCategories = [...this.cookieCategories]
			// If the cookie references a category not present in the authoritative list (e.g. custom / legacy), include it ad-hoc.
			if (cookie.categoryId && !this.modalCookieCategories.some(c => c.categoryId === cookie.categoryId)) {
				this.modalCookieCategories.push({
					categoryId: cookie.categoryId,
					categoryName: `${cookie.category || 'Custom Category'} (Custom)`
				})
			}
			this.modalCookieCategories.sort((a, b) => a.categoryName.localeCompare(b.categoryName))
			this.showCookieDetailsModal = true
		},
		closeCookieDetailsModal () {
			this.selectedCookie = null
			this.totalUrls = 0
			this.showCookieDetailsModal = false
		},
		updateCookieDetails (cookie) {
			const index = this.cookiesByDomain[this.openDomain].cookies.findIndex(c => c.cookieName === cookie.cookieName)
			this.$set(this.cookiesByDomain[this.openDomain].cookies, index, cookie)
		},
		filteredCookies (domain) {
			return domain?.cookies.filter(({ categoryId }) => {
				let check = true
				if (this.cookieCategoryFilter) check = categoryId === this.cookieCategoryFilter
				return check
			})
		},
		clearFilters () {
			this.domainFilter = null
			this.cookieCategoryFilter = 0
			this.updateOverview()
		},
		updateOverview () {
			for (const child of this.$refs.expansionPanels.$children) {
				if (child.$data.isActive) {
					child.toggle()
				}
			}
			this.cookiesByDomain = {}
			this.getCookieOverview()
		},
		dataTableItems (cookieDomain) {
			return this.cookiesByDomain[cookieDomain.domain]
				.cookies.map(cookie => ({
					...cookie,
					description: cookie.description ?? cookie.defaultCookieDescription
				}))
		}
	}
}
</script>
